import { ReactNode, useState } from 'react';
import {
  Entity,
  parseEntityRef,
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { ErrorPanel, Link, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import useAsync from 'react-use/lib/useAsync';
// ponytail: borrowed from the journeys page; move NodeCard and teamColors to a shared folder if a third page uses them
import { NodeCard } from '../../journeys/NodeCard';
import { teamColor } from '../../journeys/teamColors';
import { buildConnections } from './connections';

const useStyles = makeStyles(theme => ({
  team: {
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 4,
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'opacity 120ms',
  },
  dimmed: { opacity: 0.28 },
  services: { margin: theme.spacing(1, 0, 0), padding: 0, listStyle: 'none' },
}));

const nameOf = (ref: string) => parseEntityRef(ref).name;
const teamOf = (entity?: Entity) =>
  (entity && getEntityRelations(entity, RELATION_OWNED_BY)[0]?.name) ||
  'unowned';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

type Group = { team: string; refs: string[] };
type Hover = { team?: string; service?: string };

/** Another team: how many of its services connect to ours, and which, on click. */
const TeamCard = ({
  group,
  isDimmed,
  onHover,
}: {
  group: Group;
  isDimmed: boolean;
  onHover: (hover: Hover) => void;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <Box
      className={`${classes.team} ${isDimmed ? classes.dimmed : ''}`}
      style={{ borderLeftColor: teamColor(group.team) }}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      onKeyDown={e => e.key === 'Enter' && setOpen(!open)}
      onMouseEnter={() => onHover({ team: group.team })}
    >
      <Typography style={{ fontWeight: 600 }}>{group.team}</Typography>
      <Typography variant="caption" color="textSecondary">
        {plural(group.refs.length, 'service')}
        {open ? '' : ' · click to list'}
      </Typography>
      {open && (
        <ul className={classes.services}>
          {group.refs.map(ref => (
            <li key={ref}>
              <Link
                to={`/catalog/default/component/${nameOf(ref)}`}
                onClick={e => e.stopPropagation()}
              >
                {nameOf(ref)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
};

const Column = ({
  title,
  hint,
  empty,
  children,
}: {
  title: string;
  hint: string;
  empty?: string;
  children: ReactNode[];
}) => (
  <Box flex={1} minWidth={220}>
    <Typography style={{ fontWeight: 600 }}>{title}</Typography>
    <Typography
      variant="caption"
      color="textSecondary"
      component="div"
      gutterBottom
    >
      {hint}
    </Typography>
    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
      {children}
      {!children.length && empty && (
        <Typography variant="body2">{empty}</Typography>
      )}
    </Box>
  </Box>
);

const byTeam = (refs: string[], teams: Map<string, string>): Group[] => {
  const groups = new Map<string, string[]>();
  for (const ref of refs) {
    const team = teams.get(ref) ?? 'unowned';
    groups.set(team, [...(groups.get(team) ?? []), ref]);
  }
  return [...groups]
    .map(([team, list]) => ({ team, refs: list.sort() }))
    .sort(
      (a, b) => b.refs.length - a.refs.length || a.team.localeCompare(b.team),
    );
};

/** Our services in the middle, the teams that call them on the left, the teams they call on the right. Hover either side to see what links to what. */
export const TeamConnections = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [hover, setHover] = useState<Hover>({});
  const teamRef = stringifyEntityRef(entity);

  const { value, loading, error } = useAsync(async () => {
    const { items: owned } = await catalogApi.getEntities({
      filter: { kind: 'Component', 'relations.ownedBy': teamRef },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'spec.type',
        'relations',
      ],
    });
    const { edges, callers, callees } = buildConnections(owned);
    const external = [...callers, ...callees];
    const { items } = external.length
      ? await catalogApi.getEntitiesByRefs({
          entityRefs: external,
          fields: ['kind', 'metadata.name', 'metadata.namespace', 'relations'],
        })
      : { items: [] };
    const teams = new Map(external.map((ref, i) => [ref, teamOf(items[i])]));

    const neighbours = new Map<string, Set<string>>();
    for (const [from, to] of edges) {
      neighbours.set(from, (neighbours.get(from) ?? new Set()).add(to));
      neighbours.set(to, (neighbours.get(to) ?? new Set()).add(from));
    }
    return {
      neighbours,
      callers: byTeam(callers, teams),
      callees: byTeam(callees, teams),
      // Everything the team owns counts as "ours", but only services and whatever connects to something are shown.
      services: owned
        .filter(
          c =>
            c.spec?.type === 'service' || neighbours.has(stringifyEntityRef(c)),
        )
        .map(stringifyEntityRef)
        .sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
    };
  }, [teamRef]);

  if (error) return <ErrorPanel error={error} />;
  if (loading || !value) return <Progress />;

  const { neighbours, callers, callees, services } = value;
  const linked = (a: string, refs: string[]) =>
    refs.some(ref => neighbours.get(a)?.has(ref));
  const hovered = hover.team || hover.service;
  const allGroups = [...callers, ...callees];
  const hoveredGroup = allGroups.find(g => g.team === hover.team);
  const teamDimmed = (group: Group) =>
    (!!hover.service && !linked(hover.service, group.refs)) ||
    (!!hover.team && hover.team !== group.team);
  const serviceDimmed = (ref: string) =>
    (!!hover.team && !!hoveredGroup && !linked(ref, hoveredGroup.refs)) ||
    (!!hover.service && hover.service !== ref);

  const summary = `${plural(services.length, 'service')} · called by ${plural(callers.length, 'other team')} · call ${plural(callees.length, 'other team')}`;
  const teamCard = (group: Group) => (
    <TeamCard
      key={group.team}
      group={group}
      isDimmed={!!hovered && teamDimmed(group)}
      onHover={setHover}
    />
  );

  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {summary}. Lines are not drawn: hover a team or a service to see what it
        links to.
      </Typography>
      <Box
        display="flex"
        style={{ gap: 48, overflowX: 'auto' }}
        onMouseLeave={() => setHover({})}
      >
        <Column
          title="Teams that call us"
          hint="They depend on at least one of our services"
          empty="No other team calls these services."
        >
          {callers.map(teamCard)}
        </Column>
        <Column title={entity.metadata.name} hint="Our services">
          {services.map(ref => (
            <NodeCard
              key={ref}
              node={{
                id: ref,
                name: nameOf(ref),
                team: entity.metadata.name,
                entityName: nameOf(ref),
              }}
              isDimmed={!!hovered && serviceDimmed(ref)}
              onHover={() => setHover({ service: ref })}
            />
          ))}
        </Column>
        <Column
          title="Teams we call"
          hint="We depend on at least one of their services"
          empty="These services call no other team."
        >
          {callees.map(teamCard)}
        </Column>
      </Box>
    </>
  );
};
